/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from antd/components/menu/index.tsx
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import RcMenu, { ItemGroup } from 'rc-menu';
import classNames from 'classnames';
import omit from 'rc-util/lib/omit';
import { ConfigConsumer } from '../config-provider';
import warning from 'antd/lib/_util/warning';
import collapseMotion from 'antd/lib/_util/motion';
import { cloneElement } from 'antd/lib/_util/reactNode';
import { Menu as AntdMenu } from 'antd';
import { Icon } from '../icon';
import { SiderContext } from '../layout/Sider';
import Submenu, { SubMenuProps } from './SubMenu';
import MenuItem, { MenuItemProps } from './MenuItem';
import MenuContext, { MenuTheme } from './MenuContext';
import MenuDivider from './MenuDivider';
class InternalMenu extends React.Component {
    static defaultProps = {
        theme: 'light',
        type: 'primary'
    };
    constructor(props) {
        super(props);
        if (process.env.NODE_ENV !== 'production')
            warning(!('inlineCollapsed' in props && props.mode !== 'inline'), 'Menu', '`inlineCollapsed` should only be used when `mode` is inline.');
        warning(!(props.siderCollapsed !== undefined && 'inlineCollapsed' in props), 'Menu', '`inlineCollapsed` not control Menu under Sider. Should set `collapsed` on Sider instead.');
    }
    getInlineCollapsed() {
        const { inlineCollapsed, siderCollapsed } = this.props;
        if (siderCollapsed !== undefined) {
            return siderCollapsed;
        }
        return inlineCollapsed;
    }
    renderMenu = ({ getPopupContainer, getPrefixCls, direction, animation }) => {
        const rootPrefixCls = getPrefixCls();
        const { prefixCls: customizePrefixCls, className, theme, expandIcon, type, inlineIndent = 24, ...restProps } = this.props;
        const passedProps = omit(restProps, ['siderCollapsed', 'collapsedWidth']);
        // passedProps.inlineIndent = inlineIndent;
        const inlineCollapsed = this.getInlineCollapsed();
        const defaultMotions = {
            horizontal: { motionName: `${rootPrefixCls}-slide-up` },
            inline: collapseMotion,
            other: { motionName: `${rootPrefixCls}-zoom-big` }
        };
        const prefixCls = getPrefixCls('menu', customizePrefixCls);
        /**
         * Theme class is not needed when it is Primary Horizontal Menu because primary type is independent of dark or light theme.
         * However, the theme still applys to popup menu.
         * */
        const canApplyTheme = !(type === 'primary');
        const menuClassName = classNames({
            [`${prefixCls}-${theme}`]: canApplyTheme,
            [`${prefixCls}-primary`]: type === 'primary',
            [`${prefixCls}-breathing`]: !!animation?.breathing
        }, className);
        // B-Design: Update the expandIcon to maintain its own classes.
        const defaultExpandIcon = (React.createElement(Icon, { className: `${prefixCls}-submenu-expand-icon ${prefixCls}-submenu-expand-icon-arrow ${prefixCls}-${this.props.mode}-submenu-expand-icon`, type: 'chevron--down' }));
        let mergedExpandIcon = defaultExpandIcon;
        if (expandIcon) {
            mergedExpandIcon = cloneElement(expandIcon, (props) => {
                return { ...props, className: [`${props.className} ${prefixCls}-submenu-expand-icon`] };
            });
        }
        return (React.createElement(MenuContext.Provider, { value: {
                prefixCls,
                inlineCollapsed: inlineCollapsed || false,
                antdMenuTheme: theme,
                direction,
                firstLevel: true
            } },
            React.createElement(RcMenu, { getPopupContainer: getPopupContainer, overflowedIndicator: React.createElement(Icon, { type: 'overflow-menu--horizontal', size: 'small' }), overflowedIndicatorPopupClassName: `${prefixCls}-${theme}`, ...passedProps, inlineCollapsed: inlineCollapsed, className: menuClassName, prefixCls: prefixCls, direction: direction, defaultMotions: defaultMotions, expandIcon: mergedExpandIcon, inlineIndent: inlineIndent })));
    };
    render() {
        return React.createElement(ConfigConsumer, null, this.renderMenu);
    }
}
class Menu extends AntdMenu {
    static Divider = MenuDivider;
    static Item = MenuItem;
    static SubMenu = Submenu;
    static ItemGroup = ItemGroup;
    render() {
        return (React.createElement(SiderContext.Consumer, null, (context) => React.createElement(InternalMenu, { ...this.props, ...context })));
    }
}
export { Menu };
export default Menu;
